import React, { FC, useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import Button from 'components/Button';
import { useCreateCandidateMutation } from 'core/api/candidates';
import { useModalStore } from 'stores/modal';
import { useSessionStore } from 'stores/session';
import AgencyAdmin from 'core/models/agencyAdmin';
import { SSpacedRow } from 'styles/layout';
import TextInput from 'components/Forms/lib/TextInput';
import { OptionsProps } from 'components/Forms/lib/SelectInput';
import { SelectInput } from 'components/Forms/lib';
import { Civility } from 'core/enums/civility';
import { enumKeys } from 'utils/common';
import { REGEXP } from 'constants/validationsForm';
import { DisabledField } from 'core/useCases/modal/types';
import useResponsive from 'hooks/responsive';
import { UserRole } from 'core/enums/userRole';
import { useAgenciesQuery } from 'core/api/agencies';
import ToggleInput from 'components/Forms/lib/ToggleInput';

type Inputs = {
    civility: Civility;
    origin: string;
    email: string;
    firstName: string;
    job: string;
    lastName: string;
    mobileNumber: string;
    phoneNumber: string;
    agencyId: string;
};

type CandidateFormProps = {
    closeModal(): void;
    defaultValues?: Partial<Inputs>;
    disabledFields?: Array<DisabledField>;
};

const CandidateForm: FC<CandidateFormProps> = ({ closeModal, defaultValues }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const closeModalStore = useModalStore((s) => s.closeModal);
    const createCandidate = useCreateCandidateMutation();
    const [civilityOptions, setCivilityOptions] = useState<OptionsProps>([]);
    const user = useSessionStore((s) => s.user) as AgencyAdmin;
    const { data: agenciesData } = useAgenciesQuery();
    const agencies = useMemo(() => agenciesData ?? [], [agenciesData]);
    const agenciesOptions: OptionsProps = useMemo(
        () =>
            agencies.map((agency) => ({
                label: agency.name,
                value: agency.id,
            })),
        [agencies],
    );
    const isAdmin = UserRole.Admin === user.role;

    useEffect(() => {
        const civilityOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(Civility)) {
            civilityOptionsComputed.push({
                label: t(`common.civility.enum.${Civility[value]}`),
                value: Civility[value],
            });
        }
        setCivilityOptions(civilityOptionsComputed);
    }, [setCivilityOptions]);

    const methods = useForm<Inputs>({
        defaultValues,
    });
    const { handleSubmit, control } = methods;

    const onSubmit = handleSubmit(async (values: Inputs) => {
        try {
            const agencyId = values.agencyId ?? user.agency.id;
            await createCandidate.mutateAsync({ agencyId, inputs: values });

            closeModalStore();
            toast.success(t('candidate.creation.success'));
        } catch {
            toast.error(t('candidate.creation.error'));
            closeModalStore();
        }
    });

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                {isAdmin && (
                    <SSpacedRow>
                        <SelectInput
                            control={control}
                            label={t('common.agency.label')}
                            name="agencyId"
                            options={agenciesOptions}
                            placeholder={t('common.agency.placeholder')}
                            semi={!isMobile}
                            required
                        />
                        <ToggleInput
                            control={control}
                            label={t('common.b2c.label')}
                            name="isB2C"
                            semi={!isMobile}
                            defaultValue
                        />
                    </SSpacedRow>
                )}
                <SSpacedRow>
                    <SelectInput
                        control={control}
                        label={t('common.civility.label')}
                        name="civility"
                        options={civilityOptions}
                        placeholder={t('common.civility.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.origin.label')}
                        name="origin"
                        placeholder={t('common.origin.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <SSpacedRow>
                    <TextInput
                        label={t('common.first-name.label')}
                        name="firstName"
                        placeholder={t('common.first-name.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.last-name.label')}
                        name="lastName"
                        placeholder={t('common.last-name.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <SSpacedRow>
                    <TextInput
                        label={t('common.mobile-number.label')}
                        name="mobileNumber"
                        pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                        placeholder={t('common.mobile-number.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.phone-number.label')}
                        name="phoneNumber"
                        pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                        placeholder={t('common.phone-number.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <SSpacedRow>
                    <TextInput
                        label={t('common.email.label')}
                        name="email"
                        pattern={{ message: t(REGEXP.EMAIL.message), value: REGEXP.EMAIL.value }}
                        placeholder={t('common.email.placeholder')}
                        semi={!isMobile}
                        type="email"
                        required
                    />
                    <TextInput
                        label={t('common.job.label')}
                        name="job"
                        placeholder={t('common.job.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <BaseModal.BottomActions>
                    <Button onClick={() => closeModal()} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button submit>{t('common.create')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default CandidateForm;
